import {
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import type { WorkspaceRepositoryController } from "./WorkspaceRepository.controller.types";
import {
  type CreateWorkspaceRepositoryOptions,
  createWorkspaceRepository,
  type WorkspaceRepository,
} from "./workspace.repository";
import { WorkspaceRepositoryContext } from "./workspaceRepositoryContext";

export type { WorkspaceRepositoryController };

export type WorkspaceRepositoryProviderProps = PropsWithChildren &
  CreateWorkspaceRepositoryOptions;

const useWorkspaceRepositoryController = (
  repository: WorkspaceRepository,
): WorkspaceRepositoryController => {
  const document = useSyncExternalStore(
    repository.subscribe,
    repository.getSnapshot,
  );

  return useMemo(
    () => ({
      document,
      createWorkspace: repository.createWorkspace,
      duplicateWorkspace: repository.duplicateWorkspace,
      renameWorkspace: repository.renameWorkspace,
      reorderWorkspace: repository.reorderWorkspace,
      closeWorkspace: repository.closeWorkspace,
      reset: repository.reset,
      mutateWorkspace: repository.mutateWorkspace,
      stageWorkspaceSnapshot: repository.stageWorkspaceSnapshot,
      getPendingWorkspaceMutationStatus:
        repository.getPendingWorkspaceMutationStatus,
      recoverPendingWorkspaces: repository.recoverPendingWorkspaces,
      flush: repository.flush,
      getSnapshot: repository.getSnapshot,
      subscribe: repository.subscribe,
    }),
    [document, repository],
  );
};

const WorkspaceRepositoryConnected = ({
  children,
  repository,
}: PropsWithChildren<{ repository: WorkspaceRepository }>) => {
  const value = useWorkspaceRepositoryController(repository);
  return (
    <WorkspaceRepositoryContext.Provider value={value}>
      {children}
    </WorkspaceRepositoryContext.Provider>
  );
};

export const WorkspaceRepositoryProvider = ({
  children,
  channel,
  createChannel,
  lock,
  writerId,
  storage,
}: WorkspaceRepositoryProviderProps) => {
  const [repository, setRepository] = useState<WorkspaceRepository | null>(
    null,
  );

  useEffect(() => {
    const created = createWorkspaceRepository({
      channel,
      createChannel,
      lock,
      writerId,
      storage,
    });
    setRepository(created);
    return () => created.dispose();
  }, [channel, createChannel, lock, storage, writerId]);

  if (!repository || repository.isDisposed()) {
    return null;
  }

  return (
    <WorkspaceRepositoryConnected repository={repository}>
      {children}
    </WorkspaceRepositoryConnected>
  );
};

export const useWorkspaceRepository = (): WorkspaceRepositoryController => {
  const value = useContext(WorkspaceRepositoryContext);
  if (!value) {
    throw new Error(
      "useWorkspaceRepository must be used within WorkspaceRepositoryProvider",
    );
  }
  return value;
};

export const useOptionalWorkspaceRepository =
  (): WorkspaceRepositoryController | null =>
    useContext(WorkspaceRepositoryContext);
